import { createContext, useContext, useEffect } from "react";
import { PageBack } from "./pageDefinition.types";

export interface PanelPageContextValue {
  /** Replaces the page bar's title; undefined puts the definition's back. */
  setPageTitle: (title: string | undefined) => void;
  /** Replaces this panel's title, "" for none; undefined puts the definition's back. */
  setPanelTitle: (title: string | undefined) => void;
  /** Replaces the page bar's back button; undefined puts the definition's back. */
  setPageBack: (back: PageBack | undefined) => void;
}

export const PanelPageContext = createContext<PanelPageContextValue | null>(null);

/**
 * Renames the page and this panel from inside a panel, for a page whose title depends on
 * what it shows. Either left undefined keeps the definition's title.
 */
export function usePanelPageTitles({
  page,
  panel,
  back,
}: {
  page?: string;
  panel?: string;
  back?: PageBack;
}) {
  const context = useContext(PanelPageContext);
  const setPageTitle = context?.setPageTitle;
  const setPanelTitle = context?.setPanelTitle;
  const setPageBack = context?.setPageBack;
  const backRoute = back?.route;
  const backTitle = back?.title;

  useEffect(() => {
    setPageBack?.(
      backRoute && backTitle
        ? { route: backRoute, title: backTitle }
        : undefined
    );
    return () => setPageBack?.(undefined);
  }, [setPageBack, backRoute, backTitle]);

  useEffect(() => {
    setPageTitle?.(page);
    return () => setPageTitle?.(undefined);
  }, [setPageTitle, page]);

  useEffect(() => {
    setPanelTitle?.(panel);
    return () => setPanelTitle?.(undefined);
  }, [setPanelTitle, panel]);
}
